import { createLookupTable } from "/lib";
import { BikeAvailabilityStatus, BikeModel, getBikeModelAvailability } from "/lib/bike";
import { BikeModelsQ } from "/lib/sanity/queries/bikeModels";
import { ImageQ } from "/lib/sanity/queries/common";
import { Subscription } from "/lib/subscription";

export type Variant = {
  type: "short" | "long";
  title: string;
  description: string;
  passengers: number;
};

export const BIKE_VARIANTS: Record<"long" | "short", Variant> = {
  long: {
    type: "long",
    title: "Lange modeller:",
    description: "Plass til last og 2 passasjerer",
    passengers: 2
  },
  short: {
    type: "short",
    title: "Korte modeller:",
    description: "Plass til last og 1 passasjer",
    passengers: 1
  }
};

export type MassagedBikeAndSub = Omit<BikeModel, "subscriptions"> & {
  subscriptions: Extract<Subscription, { type: "bike" }>[] | null;
  image: ImageQ;
  pageLink: {
    href: string;
  };
  availability: BikeAvailabilityStatus;
};

// Utility function to please typescript
function removeAccessory(val: Subscription): val is Extract<Subscription, { type: "bike" }> {
  // This is to make sure the e2e tests are working.
  // It happens because the tests get mockData that could be better
  if (typeof val === "undefined") {
    return false;
  }
  return val.type !== "accessory";
}

export const massageBikeAndSubData = (
  bikes: BikeModel[],
  subs: Subscription[],
  bikeModelsTable: Record<string, BikeModelsQ[0]>,
  availabilityTable: Record<string, BikeAvailabilityStatus>
) => {
  // Creates lookup table for subs
  const subsTable: Record<string, Subscription> = createLookupTable(subs, "id");

  const outputData: MassagedBikeAndSub[] = bikes
    .filter((bike) => {
      if (bike.id in bikeModelsTable) {
        return true;
      }
      return false;
    })
    .map((bike) => {
      const image: ImageQ = bikeModelsTable[bike.id].image;

      const pageLink: { href: string } = {
        href: bikeModelsTable[bike.id]["linkItems"]?.[0]["href"] || ""
      };

      const availability = availabilityTable[bike.id];

      if (!bike.subscriptions) {
        return {
          ...bike,
          subscriptions: null,
          image: image,
          pageLink: pageLink,
          availability: availability
        };
      }

      const subscriptions = bike.subscriptions
        .map((id) => subsTable[id])
        .filter(removeAccessory)
        .filter((sub) => !sub.archived)
        .sort((a, b) => a.price - b.price);

      return {
        ...bike,
        subscriptions: subscriptions,
        image: image,
        pageLink: pageLink,
        availability: availability
      };
    });

  // Sort the bikes by the lowest subscription price
  // At this point, we know that the first element of subscriptions is the lowest price for each bike type.
  outputData.sort(
    (a, b) => (a.subscriptions?.at(0)?.price ?? 0) - (b.subscriptions?.at(0)?.price ?? 0)
  );

  return outputData;
};

export function createAvailabilityLookupTable(
  bikeModels: BikeModel[]
): Record<string, BikeAvailabilityStatus> {
  const table: ReturnType<typeof createAvailabilityLookupTable> = {};

  for (const bike of bikeModels) {
    table[bike.id] = getBikeModelAvailability(bike);
  }

  return table;
}
